'use client';

import { useEffect, useRef } from 'react';

/**
 * Dismisses an overlay when the user clicks outside its container.
 *
 * @param ref       React ref attached to the overlay/container node.
 * @param callback  Fired when a click lands outside `ref.current`.
 * @param active    When `false`, the listener is torn down (avoids
 *                  unnecessary document listeners when the overlay is closed).
 * @param delay     Optional ms to wait before calling `callback`
 *                  (matches the existing 200 ms debounce used by most cards).
 */
export function useClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  callback: () => void,
  active = true,
  delay = 0,
): void {
  // Keep the latest callback in a ref so the effect doesn't need to
  // re-register the document listener every time the callback identity changes.
  const savedCallback = useRef(callback);
  savedCallback.current = callback;

  useEffect(() => {
    if (!active) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        if (delay > 0) {
          setTimeout(() => savedCallback.current(), delay);
        } else {
          savedCallback.current();
        }
      }
    };

    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [ref, active, delay]);
}
